import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { fields } from "@modules/ECMS/MAGP/Forms/FAQ/constants";
import { useFAQDocumentDetails } from "@modules/ECMS/MAGP/Forms/FAQ/Faq.hook";
import { decodeHTML, encodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { magpHost } from "~/Utils";

export const AddEditNewFAQ = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();

  const {
    data: editFaq,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchEditFaq,
  } = useFAQDocumentDetails(query.get("editId") || "");

  const {
    data: FaqById,
    isLoading: isLoadingFaq,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchEditFaq();
    }
  }, [query.get("editId"), query.get("id")]);

  const faqFormConfig: FormConfig = {
    initialValues: {
      field_order_sequence: FaqById
        ? FaqById.payload.field_order_sequence
        : editFaq
          ? editFaq.field_order_sequence
          : "",
      field_faqs_question: FaqById ? decodeHTML(FaqById.payload.field_faqs_question) : editFaq ? decodeHTML(editFaq.field_faqs_question) : "",
      field_malay_faqs_question: FaqById
        ? decodeHTML(FaqById.payload.field_malay_faqs_question)
        : editFaq
          ? decodeHTML(editFaq.field_malay_faqs_question)
          : "",
      field_mandarin_faqs_question: FaqById
        ? decodeHTML(FaqById.payload.field_mandarin_faqs_question)
        : editFaq
          ? decodeHTML(editFaq.field_mandarin_faqs_question)
          : "",
      field_faqs_answer: FaqById ? decodeHTML(FaqById.payload.field_faqs_answer) : editFaq ? decodeHTML(editFaq.field_faqs_answer) : "",
      field_malay_faqs_answer: FaqById
        ? decodeHTML(FaqById.payload.field_malay_faqs_answer)
        : editFaq
          ? decodeHTML(editFaq.field_malay_faqs_answer)
          : "",
      field_mandarin_faqs_answer: FaqById
        ? decodeHTML(FaqById.payload.field_mandarin_faqs_answer)
        : editFaq
          ? decodeHTML(editFaq.field_mandarin_faqs_answer)
          : "",
      field_faq_published: FaqById ? FaqById.payload.field_faq_published : editFaq ? editFaq.field_faq_published : "",
    },
    fields: fields.faq,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    // for (let key in data) {
    //   if (typeof data[key] === "string" && !excludedKeys.includes(key)) {
    //     data[key] = encodeHTML(data[key]);
    //   }
    // }
    let imageKeys: any = [
      "fbImage",
      "field_purchase_login_pop_image",
      "field_login_image",
      "filledImage",
      "googleImage",
      "showImage",
      "field_logout_image",
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "MAGP");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "FAQ",
      subModule: "faq",
      type: "DEDICATED",
      application: "MAGP",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
        field_faqs_header: "FAQ",
        field_malay_faqs_header: "",
        field_mandarin_faqs_header: "",
        field_faq_created_by:
          method === "POST"
            ? user.email
            : editFaq?.field_faq_created_by
              ? editFaq?.field_faq_created_by
              : FaqById?.payload?.field_faq_created_by,
        field_faq_updated_by: user.email,
        published: true 
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response:any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response:any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      // loading={isLoadingFaq}
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} FAQ`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingFaq || drilldownLoading}
        config={faqFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${magpHost}/api/faq-details${editFaq?.id ? `/${editFaq.id}` : ""}`,
            `${query.get("editId") ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </ContentWrapper>
  );
};
